import { useLoaderData, useRevalidator } from "react-router";

import ApiErrorComponent from "@/components/common/api-error";
import Button from "@/components/common/input/button";
import Modal from "@/components/common/modal";
import CompetencyForm from "@/components/forms/competency";
import { handleResponse, request } from "@/hooks/request";
import type ApiError from "@/models/ApiError";
import type { Competency } from "@/types/competency";

type LoaderData = {
  competencies: Competency[] | null;
  error: ApiError | null;
};

export async function loader(): Promise<LoaderData> {
  const { data, error } = handleResponse<Competency[]>(await request("/api/competency"));
  return { competencies: data, error };
}

export default function Competencies() {
  const { competencies, error } = useLoaderData() as LoaderData;
  const revalidator = useRevalidator();
  function reloadCompetencies() {
    void revalidator.revalidate();
  }
  if (error || !competencies) {
    return <ApiErrorComponent error={error} />;
  }

  return (
    <div className="mx-auto max-w-[60rem]">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-medium">Competencies</h2>
        <Modal>
          <Button>Add competency</Button>
          <CompetencyForm callbackFn={reloadCompetencies} />
        </Modal>
      </div>
      <div className="grid grid-cols-2">
        <div>
          <h3 className="mb-4 text-xl">Unfold skills/competencies</h3>
          {competencies
            .filter((comp) => !comp.is_evali)
            .map((comp) => {
              return (
                <CompetencyRow key={comp.id} competency={comp} callbackFn={reloadCompetencies} />
              );
            })}
        </div>
        <div>
          <h3 className="mb-4 text-xl">evali competencies</h3>
          {competencies
            .filter((comp) => comp.is_evali)
            .map((comp) => {
              return (
                <CompetencyRow key={comp.id} competency={comp} callbackFn={reloadCompetencies} />
              );
            })}
        </div>
      </div>
    </div>
  );
}

interface CompetencyRowProps {
  competency: Competency;
  callbackFn: () => void;
}

function CompetencyRow({ competency, callbackFn }: CompetencyRowProps) {
  return (
    <div className="flex items-center gap-4">
      {competency.name}{" "}
      {!competency.is_evali && (
        <Modal>
          <div className="flex items-center text-sm text-slate-400 hover:text-slate-200">
            <span className="material-symbols-rounded notranslate pr-1">edit</span>
            Edit competency
          </div>
          <CompetencyForm id={competency.id} callbackFn={callbackFn} />
        </Modal>
      )}
    </div>
  );
}
